/* eslint-disable complexity */
import React, { forwardRef } from 'react'
import { Box, Flex } from '@chilipiper/design-system'
import { Button, IconButton } from '@chilipiper/design-system/src/new'
import { SvgRemove } from '@chilipiper/icons/src/design-system'
import { sanitize } from '@chilipiper/utils'
import {
  ConversationMessage,
  ConversationAction,
  ReplyButton,
  MessagePart,
} from '@chilipiper/api-client/src/chat-platform-chat'
import { useMessageFeedContext } from '../../chat-widget/MessageFeedContext'
import { ThemedText } from '../themed-components/themedComponents'
import { ConsentFooter } from '../ConsentFooter'
import { StyledAvatarImage } from '../styles'
import { StyledContainer } from './styles'

interface Props {
  action?: ConversationAction
  messages?: ConversationMessage[]
  onCallToActionClick: () => void
  onClose?: () => void
  onReplyButtonClick?: (reply: ReplyButton, index: number) => void
}

export const BotPreview = forwardRef<HTMLDivElement, Props>(
  ({ messages, action, onClose, onReplyButtonClick, onCallToActionClick }: Props, ref) => {
    const { channel, disableInteractions } = useMessageFeedContext()

    if (!channel) {
      return null
    }

    const messagesContents = messages?.reduce<Array<MessagePart>>((acc, message) => {
      if (message.type === 'Content') {
        return [...acc, ...message.value]
      }
      return acc
    }, [])

    const textBlocks = messagesContents?.filter(block => block.type === 'Text') ?? []

    return (
      <StyledContainer
        inert={disableInteractions}
        ref={ref}
        w='368px'
        borderRadius={12}
        data-test-id='BotPreview'
        overflow='hidden'
      >
        <Flex flexDirection='column'>
          <BotPreviewHeader avatars={channel.avatars} name={channel.fullName} onClose={onClose} />
          {textBlocks.map((textBlock, index) => (
            <ThemedText
              key={index}
              color='fg/major'
              textStyle='body-multiline'
              display='block'
              whiteSpace='pre-wrap'
              px={6}
              pt={0}
              pb={index === textBlocks.length - 1 ? 6 : 1}
              data-test-id='BotPreview-text'
              dangerouslySetInnerHTML={{
                __html: sanitize(textBlock.text?.trim() ?? '', ['a'], ['target']),
              }}
            />
          ))}
          <Flex flexDirection='column' gap={2} px={4}>
            {action?.type === 'SelectReplyAction' ? (
              action.replies.map((reply, index) => {
                return (
                  <ReplyButtonComponent
                    key={index}
                    text={reply.text}
                    onClick={() => onReplyButtonClick?.(reply, index)}
                  />
                )
              })
            ) : (
              <ReplyButtonComponent text='Hello 👋' onClick={() => onCallToActionClick()} />
            )}
          </Flex>
        </Flex>
        <Box px={3} py={2}>
          <ConsentFooter />
        </Box>
      </StyledContainer>
    )
  }
)

const ReplyButtonComponent = ({ text, onClick }: { onClick: () => void; text: string }) => {
  return (
    <Button variant='moderate' onClick={onClick} data-test-id='BotPreview-replyButton' isFullWidth>
      {text}
    </Button>
  )
}

BotPreview.displayName = 'BotPreview'

const BotPreviewHeader = ({
  avatars,
  name,
  onClose,
}: {
  avatars: string[]
  name: string
  onClose?: () => void
}) => (
  <Flex justifyContent='space-between' p={6} pb={4}>
    <Flex flexDirection='column' gap={3}>
      {avatars.map((avatar, index) => (
        <Flex key={avatar} ml={index === 0 ? 0 : -3}>
          <StyledAvatarImage src={avatar} alt='' borderWidth={index === 0 ? '0' : '2px'} />
        </Flex>
      ))}
      <ThemedText color='fg/major' textStyle='heading-m' data-test-id='WidgetHeader-name'>
        {name}
      </ThemedText>
    </Flex>
    <IconButton
      size={7}
      label='Close chat'
      icon={SvgRemove}
      onClick={onClose}
      data-test-id='WidgetHeader-close'
      variant='ghost'
    />
  </Flex>
)
